import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useIsGlobetrotter, useIsMobile } from '@bwoty-web/ui-kit/hooks';
import { BackLink, Breadcrumbs, GridBleed } from '@bwoty-web/ui-kit';

import FadeIn from '../utils/fadeIn';
import CabinList from '../components/cabinList/CabinList';
import PriceSummary from '../components/shared/priceSummary/PriceSummary';
import GlobalContext from '../context/globalContext';
import LoaderHeading from '../components/shared/loaders/LoaderHeading';
import MessageContainer from '../components/shared/message/MessageContainer';
import HeadingContainer from '../components/shared/headingContainer/HeadingContainer';
import CruisePackageInfo from '../components/cabinList/CruisePackageInfo';
import { texts } from '../assets/texts';
import { urlUtils } from '../utils/url';

import { init } from '../redux/actions/cabinListActions';
import { cabinSelected } from '../redux/reducers/cabinList/cruise';
import {
  selectAccommodation,
  selectCruise,
  selectIsInitialized,
  selectLabels,
  selectMessage,
  selectPriceDetails,
  selectSearchInformation,
  selectSiteSettings,
  selectTransport,
  selectUserId,
} from '../redux/selectors/cabinList';

import './cabinListContainer.scss';

// No props, one field per `useSelector`.
const CabinListContainer = () => {
  const dispatch = useDispatch();
  const accommodation = useSelector(selectAccommodation);
  const cruise = useSelector(selectCruise);
  const initialized = useSelector(selectIsInitialized);
  const labels = useSelector(selectLabels);
  const message = useSelector(selectMessage);
  const priceDetails = useSelector(selectPriceDetails);
  const searchInformation = useSelector(selectSearchInformation);
  const siteSettings = useSelector(selectSiteSettings);
  const transport = useSelector(selectTransport);
  const userId = useSelector(selectUserId);

  const { cabins, duration: cruiseDuration, promoLabel, selectedCabinKey, totalDuration } = cruise;

  const [breadCrumbSummaryLink, setBreadCrumbSummaryLink] = useState('');
  const isGlobetrotter = useIsGlobetrotter();
  const isMobile = useIsMobile();

  useEffect(() => {
    dispatch(init());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedCabinKey || breadCrumbSummaryLink) return;

    const selectedCabin = cabins?.find(
      ({ key, fareTypePrices }) =>
        key === selectedCabinKey || fareTypePrices?.some(({ key: fareKey }) => fareKey === selectedCabinKey),
    );
    if (!selectedCabin) return;

    const summaryLink =
      selectedCabin.fareTypePrices?.find(({ key }) => key === selectedCabinKey)?.summaryLink ??
      selectedCabin.summaryLink ??
      '';

    setBreadCrumbSummaryLink(summaryLink);
  }, [selectedCabinKey, cabins, breadCrumbSummaryLink]);

  const globals = useMemo(
    () => ({
      labels,
      siteSettings,
    }),
    [siteSettings, labels],
  );

  // If no search has been done.
  // E.g. on cruise details page in inspiration mode.
  if (siteSettings.noSearch) {
    return null;
  }

  if (!initialized) {
    return (
      <LoaderHeading
        heading={texts[siteSettings.siteId].loaderHeadingCabinList}
        topMargin
      />
    );
  }

  const isChangeCabinPage = urlUtils.isChangeCabinPage();
  const productAndPaxInfo = `${labels.productsIncludedParsed}. ${searchInformation?.passengerInfo}`;
  const selectedCabinBase = cabins?.find(
    ({ key, fareTypePrices }) =>
      key === selectedCabinKey || fareTypePrices?.some(({ key: fareKey }) => fareKey === selectedCabinKey),
  );
  const selectedDrinkPackage = selectedCabinBase?.fareTypePrices?.find(({ key }) => key === selectedCabinKey);
  const selectedCabin = selectedDrinkPackage
    ? {
        ...selectedCabinBase,
        summaryLink: selectedDrinkPackage.summaryLink,
        totalPrice: selectedDrinkPackage.totalPrice,
        totalPriceValue: selectedDrinkPackage.totalPriceValue,
      }
    : selectedCabinBase;

  const DividerElement = isMobile ? GridBleed : 'div';

  return (
    <GlobalContext.Provider value={globals}>
      <FadeIn>
        <div className="iw-cabin-list-container">
          <MessageContainer
            message={message}
            user={userId}
            labels={labels}
            style={{ marginBottom: 20 }}
          />

          {isChangeCabinPage &&
            (isGlobetrotter ? (
              <>
                <Breadcrumbs
                  className="iw-cabin-list__breadcrumbs"
                  links={[
                    {
                      name: labels.iwGeneral.breadcrumbComboSummaryLinkLabel,
                      uri: urlUtils.standaloneUrlHandler(breadCrumbSummaryLink),
                      target: null,
                    },
                    {
                      name: labels.changeCabinPageHeading,
                      uri: window.location.href,
                      target: null,
                    },
                  ]}
                />
                <h1 className="iw-cabin-list__change-cabin-heading">{labels.changeCabinPageHeading}</h1>
                <div className="iw-cabin-list__heading-divider" />
              </>
            ) : (
              <HeadingContainer>
                <h1 className="iw-cabin-list__change-cabin-heading">{labels.changeCabinPageHeading}</h1>
                <BackLink href={urlUtils.standaloneUrlHandler(breadCrumbSummaryLink)}>
                  {labels.iwGeneral.backToSummary}
                </BackLink>
              </HeadingContainer>
            ))}

          {cabins && selectedCabin && (
            <>
              {!isChangeCabinPage && transport && (
                <CruisePackageInfo
                  duration={labels.packageInfoDuration
                    .replace('{totalDuration}', totalDuration)
                    .replace('{cruiseDuration}', cruiseDuration)}
                  transport={transport}
                  accommodation={accommodation}
                  promoLabel={promoLabel}
                />
              )}
              <CabinList
                cabins={cabins}
                isChangeCabinPage={isChangeCabinPage}
                selectCabin={(cabinKey) => dispatch(cabinSelected(cabinKey))}
                selectedCabinKey={selectedCabinKey}
              />
              {isGlobetrotter && !isChangeCabinPage && <DividerElement className="iw-cabin-list__divider" />}
              <PriceSummary
                discounted={priceDetails.isDiscounted}
                labels={{
                  button: isChangeCabinPage ? labels.bookButtonLabelChangeMode : labels.bookButtonLabel,
                  totalPrice: labels.totalPrice,
                  couponInfo: labels?.couponInfo,
                }}
                onNavigate={() => {
                  window.location = urlUtils.standaloneUrlHandler(`/${selectedCabin.summaryLink}`);
                }}
                productAndPaxInfo={productAndPaxInfo}
                totalPrice={selectedCabin.totalPrice}
              />
            </>
          )}
        </div>
      </FadeIn>
    </GlobalContext.Provider>
  );
};

export default CabinListContainer;
